import React from 'react';
import { useForm } from '@inertiajs/react';
import SubmitButton from '../../../components/form/SubmitButton';
import FormError from '../../../components/form/FormError';
import { useTrans } from '../../../hooks/useTrans';
import { trackAndTraceDeliveryPreferencesUrl } from '../../../lib/routes';

const LOW_REMAINING_THRESHOLD = 20;

export default function DeliveryPreferencesForm({ traceUuid, fromOrderId, deliveryPreferences, orderIsCompleted }) {
    const translate = useTrans();
    const {
        show, showPreferences, showMessageForDriver, options, detailFields, freeTextDetailMaxLength, messageMaxLength, values,
    } = deliveryPreferences;

    const { data, setData, post, processing, errors } = useForm({
        delivery_preference: values.delivery_preference ?? '',
        delivery_preference_detail: values.delivery_preference_detail,
        message_for_driver: values.message_for_driver,
    });

    if (!show) {
        return null;
    }

    const detailField = detailFields[data.delivery_preference];
    const remainingCharacters = messageMaxLength - data.message_for_driver.length;
    const splitDesktop = showPreferences && showMessageForDriver;

    const handlePreferenceChange = (value) => {
        setData((previous) => ({
            ...previous,
            delivery_preference: value,
            delivery_preference_detail: detailFields[value] ? previous.delivery_preference_detail : '',
        }));
    };

    const submit = (event) => {
        event.preventDefault();
        post(trackAndTraceDeliveryPreferencesUrl(traceUuid, { fromOrderId }), { preserveScroll: true });
    };

    return (
        <div className="track-trace-delivery-preferences">
            <form
                onSubmit={submit}
                className={`track-trace-delivery-preferences__form track-trace-transport-preferences__form${splitDesktop ? ' track-trace-delivery-preferences__form--split-desktop' : ''}`}
            >
                {showPreferences && (
                    <div className="track-trace-delivery-preferences__col track-trace-delivery-preferences__col--preferences">
                        <div className="form-item form-item--vertical-layout track-trace-transport-preferences__field track-trace-delivery-preferences__field">
                            <label className="form-item__label" htmlFor="track-trace-delivery-preference">
                                {translate('Bezorgvoorkeuren')}
                            </label>
                            <select
                                className="form-item__input form-item__select track-trace-delivery-preferences__control"
                                name="delivery_preference"
                                id="track-trace-delivery-preference"
                                value={data.delivery_preference}
                                disabled={orderIsCompleted}
                                onChange={(event) => handlePreferenceChange(event.target.value)}
                            >
                                <option value="">{translate('Maak een keuze')}</option>
                                {options.map((option) => (
                                    <option key={option.value} value={option.value}>{translate(option.label)}</option>
                                ))}
                            </select>

                            <FormError message={errors.delivery_preference} />
                        </div>

                        <div className={`form-item form-item--vertical-layout track-trace-transport-preferences__field track-trace-delivery-preferences__field${detailField ? '' : ' hide'}`}>
                            <label className="form-item__label" htmlFor="track-trace-delivery-preference-detail">
                                {detailField ? detailField.label : translate('Toelichting bij afleverplek')}
                            </label>
                            <input
                                type="text"
                                className={`form-item__input track-trace-delivery-preferences__control${detailField?.compact ? ' track-trace-delivery-preferences__control--compact' : ''}`}
                                name="delivery_preference_detail"
                                id="track-trace-delivery-preference-detail"
                                value={data.delivery_preference_detail}
                                maxLength={detailField ? detailField.max_length : freeTextDetailMaxLength}
                                autoComplete={detailField ? detailField.autocomplete : 'off'}
                                pattern={detailField?.pattern ?? undefined}
                                title={detailField?.pattern_message ?? undefined}
                                aria-describedby="track-trace-delivery-preference-detail-hint"
                                disabled={orderIsCompleted}
                                onChange={(event) => setData('delivery_preference_detail', event.target.value)}
                            />
                            <p
                                id="track-trace-delivery-preference-detail-hint"
                                className={`track-trace-delivery-preferences__hint${detailField?.hint ? '' : ' hide'}`}
                            >
                                {detailField?.hint ?? ''}
                            </p>

                            <FormError message={errors.delivery_preference_detail} />
                        </div>
                    </div>
                )}

                {showMessageForDriver && (
                    <div className="track-trace-delivery-preferences__col track-trace-delivery-preferences__col--message">
                        <div className="form-item form-item--vertical-layout track-trace-transport-preferences__field track-trace-delivery-preferences__field">
                            <label className="form-item__label form-item__label--textarea" htmlFor="track-trace-message-driver">
                                {translate('Bericht achterlaten voor chauffeur')}
                            </label>
                            <textarea
                                className="form-item__textarea track-trace-delivery-preferences__control track-trace-delivery-preferences__textarea"
                                name="message_for_driver"
                                id="track-trace-message-driver"
                                rows={4}
                                maxLength={messageMaxLength}
                                value={data.message_for_driver}
                                disabled={orderIsCompleted}
                                onChange={(event) => setData('message_for_driver', event.target.value)}
                            />
                            <p
                                role="status"
                                aria-live="polite"
                                className={`track-trace-delivery-preferences__char-counter${remainingCharacters <= LOW_REMAINING_THRESHOLD ? ' track-trace-delivery-preferences__char-counter--low' : ''}`}
                            >
                                {translate('Nog :remaining tekens', { remaining: remainingCharacters })}
                            </p>

                            <FormError message={errors.message_for_driver} />
                        </div>
                    </div>
                )}

                {!orderIsCompleted && (
                    <div className="form-item track-trace-transport-preferences__submit track-trace-delivery-preferences__submit">
                        <SubmitButton title={translate('Opslaan')} disabled={processing} />
                    </div>
                )}
            </form>
        </div>
    );
}
